import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  cancelSubscription,
  listSubscriptions,
  pauseSubscription,
  resumeSubscription,
  type Subscription,
} from '@/lib/api/subscriptions.js';
import { NewSubscriptionSlideOver } from '@/components/subscriptions/NewSubscriptionSlideOver.js';
import { CollectSubscriptionPaymentModal } from '@/components/subscriptions/CollectSubscriptionPaymentModal.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

function statusBadgeClass(status: string) {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'paused':
      return 'bg-amber-100 text-amber-800';
    case 'past_due':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function CustomerSubscriptionsSection({ customerId }: { customerId: string }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [collectSub, setCollectSub] = useState<Subscription | null>(null);

  const subsQuery = useQuery({
    queryKey: ['subscriptions', { customerId }],
    queryFn: () => listSubscriptions({ customerId, page: 1, pageSize: 50 }),
  });

  const subscriptions = subsQuery.data?.data ?? [];

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
  }

  const pauseMut = useMutation({
    mutationFn: (id: string) => pauseSubscription(id),
    onSuccess: () => {
      toastSuccess('Subscription paused');
      refresh();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const resumeMut = useMutation({
    mutationFn: (id: string) => resumeSubscription(id),
    onSuccess: () => {
      toastSuccess('Subscription resumed');
      refresh();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelSubscription(id),
    onSuccess: () => {
      toastSuccess('Subscription cancelled');
      refresh();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function handleCancel(sub: Subscription) {
    if (!window.confirm(`Cancel subscription "${sub.name}"?`)) return;
    cancelMut.mutate(sub.id);
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <h3 className="text-lg font-semibold text-slate-950">Subscriptions</h3>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#164477]"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      <div className="divide-y divide-slate-100">
        {subscriptions.map((sub) => (
          <div
            key={sub.id}
            className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-950">{sub.name}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-bold uppercase',
                    statusBadgeClass(sub.status),
                  )}
                >
                  {prettyLabel(sub.status)}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {formatCurrency(sub.amount)} · {prettyLabel(sub.frequency)} · Next{' '}
                {formatDate(sub.nextBillingDate)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sub.status === 'active' || sub.status === 'past_due' ? (
                <button
                  type="button"
                  className="rounded-xl border border-primary px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/5"
                  onClick={() => setCollectSub(sub)}
                >
                  Collect
                </button>
              ) : null}
              {sub.status === 'active' ? (
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={pauseMut.isPending}
                  onClick={() => pauseMut.mutate(sub.id)}
                >
                  Pause
                </button>
              ) : null}
              {sub.status === 'paused' ? (
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={resumeMut.isPending}
                  onClick={() => resumeMut.mutate(sub.id)}
                >
                  Resume
                </button>
              ) : null}
              {sub.status !== 'cancelled' ? (
                <button
                  type="button"
                  className="rounded-xl border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                  disabled={cancelMut.isPending}
                  onClick={() => handleCancel(sub)}
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        ))}
        {!subscriptions.length && !subsQuery.isLoading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No subscriptions yet.</div>
        ) : null}
        {subsQuery.isLoading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : null}
      </div>

      <NewSubscriptionSlideOver
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
        defaultCustomerId={customerId}
      />

      <CollectSubscriptionPaymentModal
        open={Boolean(collectSub)}
        subscription={collectSub}
        onClose={() => setCollectSub(null)}
        onSuccess={refresh}
      />
    </section>
  );
}
